import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Container, Button } from "react-bootstrap";
import "../../Pages/AddFund/AddFund.css";
import apiCall from "./../../API/API";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import debounce from "lodash.debounce";
import { BiFilter } from "react-icons/bi";
import { GrPowerReset } from "react-icons/gr";
import PaginationComponent from "../../Component/Pagination/PaginationControls";
import { toastFailed } from "../../Common/Data";
import ConfirmModal from "../../Component/ConfirmModel";

const AdminBuyOrder = () => {
  const [userId, setUserId] = useState("");
  const [existUserName, setExistUserName] = useState({});
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [amount, setAmount] = useState("");
  const [minAmount, setMinAmount] = useState(0);
  const [maxAmount, setMaxAmount] = useState(0);

  const [addedDate, setAddedDate] = useState("");
  const [addedTime, setAddedTime] = useState("");

  const [fakeOrders, setFakeOrders] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limitFilter, setLimitFilter] = useState(20);
  const [filterDisplay, setFilterDisplay] = useState("none");
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalSum, setTotalSum] = useState(0);

  const [searchParams, setSearchParams] = useState({
    username: "",
    planId: "",
    minAmountFilter: "",
    maxAmountFilter: "",
    startDate: "",
    endDate: "",
    status: "",
    search: "",
    uid: "",
  });
  const [dateFilterQuickSelect, setDateFilterQuickSelect] = useState("default");

  const [showDeleteSingleModal, setShowDeleteSingleModal] = useState(false);
  const [selectedOrderIdToDelete, setSelectedOrderIdToDelete] = useState(null);

  const token = localStorage.getItem("authToken");

  useEffect(() => {
    const fetchPackages = async () => {
      setLoading(true);
      try {
        const { data, error } = await apiCall("GET", "/getAllPackage");
        if (data && data.packages) {
          setPackages(data.packages);
        } else if (error) {
          toast.error(error.message || "Failed to fetch packages.");
        }
      } catch (err) {
        toast.error("An error occurred while fetching packages.");
        console.error("Fetch Packages Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  const fetchFakeOrders = useCallback(
    debounce(async (params) => {
      setTableLoading(true);
      try {
        const { data, error } = await apiCall("GET", `/get-both-orders?${new URLSearchParams(params).toString()}`,{
          headers: {
            Authorization: token,
          },
        });
        if (data && data.data) {
          setFakeOrders(data.data);
          setTotalPages(data.totalPages || 1);
          setTotalRecords(data.totalRecords || 0);
          setTotalSum(data.Sum || 0);
        } else if (error) {
          toast.error(error.message || "Failed to fetch fake orders.");
        }
      } catch (err) {
        toast.error("An error occurred while fetching fake orders.");
        console.error("Fetch Fake Orders Error:", err);
      } finally {
        setTableLoading(false);
      }
    }, 500),
    [token]
  );

  useEffect(() => {
    const queryParams = {
      page: currentPage,
      limit: limitFilter,
      // Ensure startDate and endDate are always included, even if empty
      startDate: searchParams.startDate,
      endDate: searchParams.endDate,
      // Filter other searchParams that are empty or 0
      ...Object.fromEntries(
        Object.entries(searchParams).filter(
          ([key, value]) =>
            value !== "" && value !== 0 && key !== "startDate" && key !== "endDate"
        )
      ),
    };
    fetchFakeOrders(queryParams);
  }, [currentPage, limitFilter, searchParams, fetchFakeOrders]);

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    if (userId) {
      setTypingTimeout(
        setTimeout(() => {
          checkUserExistence();
        }, 500)
      );
    } else {
      setExistUserName({});
    }

    return () => clearTimeout(typingTimeout);
  }, [userId]);

  const checkUserExistence = async () => {
    setLoading(true);
    try {
      const body = { username: userId };
      const { data, error } = await apiCall("post", "/check_username", body);
      if (data) {
        setExistUserName({ name: data.name, color: "var(--colorSuccess)" });
      } else if (error) {
        setExistUserName({ name: "User not exists", color: "var(--redIcon)" });
      }
    } catch (err) {
      toast.error("An error occurred while checking user existence.");
      console.error("User Existence Check Error:", err);
      setExistUserName({ name: "Error checking user", color: "var(--redIcon)" });
    } finally {
      setLoading(false);
    }
  };

  const handlePackageSelect = (e) => {
    const selectedPlanId = e.target.value;
    const pkg = packages.find(p => p.planId.toString() === selectedPlanId);
    if (pkg) {
      setSelectedPackage(pkg);
      setMinAmount(pkg.package.min_amount);
      setMaxAmount(pkg.package.max_amount);
      setAmount(pkg.package.min_amount);
    } else {
      setSelectedPackage(null);
      setMinAmount(0);
      setMaxAmount(0);
      setAmount("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!userId || existUserName.color !== "var(--colorSuccess)") {
      toast.error("Please enter a valid existing username.");
      setLoading(false);
      return;
    }
    if (!selectedPackage) {
      toast.error("Please select a package.");
      setLoading(false);
      return;
    }
    if (amount < minAmount || amount > maxAmount) {
      toast.error(`Amount must be between ${minAmount} and ${maxAmount}.`);
      setLoading(false);
      return;
    }
    if (!addedDate) {
      toast.error("Please select a date for 'Added On'.");
      setLoading(false);
      return;
    }
    if (!addedTime) {
      toast.error("Please select a time for 'Added On'.");
      setLoading(false);
      return;
    }

    const addedOnDateTime = new Date(`${addedDate}T${addedTime}`);

    const requestBody = {
      username: userId,
      planId: selectedPackage.planId,
      amount: parseFloat(amount),
      added_on: addedOnDateTime.toISOString(),
    };

    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: token,
        },
      };
      const { data, error } = await apiCall(
        "post",
        "/user-buyOrder-fromAdmin",
        requestBody,
        config
      );

      if (data) {
        toast.success(data.message || "Order placed successfully for user!");
        handleClearForm();
        // After placing an order, refresh the table by triggering the fetch
        const queryParams = {
          page: currentPage,
          limit: limitFilter,
          startDate: searchParams.startDate, // Ensure these are included for refresh
          endDate: searchParams.endDate,     // Ensure these are included for refresh
          ...Object.fromEntries(
            Object.entries(searchParams).filter(
              ([key, value]) =>
                value !== "" && value !== 0 && key !== "startDate" && key !== "endDate"
            )
          ),
        };
        fetchFakeOrders(queryParams);
      } else if (error) {
        toast.error(error.message || "Failed to place order for user.");
      }
    } catch (err) {
      toast.error("An error occurred during order placement.");
      console.error("Admin Buy Order Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearForm = () => {
    setUserId("");
    setExistUserName({});
    setSelectedPackage(null);
    setAmount("");
    setMinAmount(0);
    setMaxAmount(0);
    setAddedDate("");
    setAddedTime("");
  };

  const toggleFilter = () => {
    setFilterDisplay(filterDisplay === "none" ? "flex" : "none");
  };

  const handlePagination = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchParams((prev) => ({ ...prev, [name]: value }));
  };

  const handleLimitChange = (e) => {
    setLimitFilter(parseInt(e.target.value, 20));
    setCurrentPage(1);
  };

  const handleDateFilterQuickSelectChange = (e) => {
    const value = e.target.value;
    setDateFilterQuickSelect(value);
    const today = moment();
    let start = "";
    let end = "";

    switch (value) {
      case "today":
        start = today.clone().startOf("day").format("YYYY-MM-DD");
        end = today.format("YYYY-MM-DD");
        break;
      case "yesterday":
        start = today.clone().subtract(1, "days").startOf("day").format("YYYY-MM-DD");
        end = today.clone().subtract(1, "days").endOf("day").format("YYYY-MM-DD");
        break;
      case "this_week":
        start = today.clone().startOf("week").format("YYYY-MM-DD");
        end = today.clone().endOf("week").format("YYYY-MM-DD");
        break;
      case "this_month":
        start = today.clone().startOf("month").format("YYYY-MM-DD");
        end = today.clone().endOf("month").format("YYYY-MM-DD");
        break;
      default:
        start = "";
        end = "";
        break;
    }
    setSearchParams((prev) => ({ ...prev, startDate: start, endDate: end }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    // The useEffect will pick up the searchParams change after setCurrentPage(1)
    // and trigger the fetch. No explicit fetch here is needed because of useEffect.
  };

  const resetFilters = () => {
    setSearchParams({
      username: "",
      planId: "",
      minAmountFilter: "",
      maxAmountFilter: "",
      startDate: "",
      endDate: "",
      status: "",
      search: "",
      uid: "",
    });
    setLimitFilter(20);
    setDateFilterQuickSelect("default");
    setCurrentPage(1);
  };

  const handleDeleteAllFakeOrders = async () => {
    try {
      const { data, error } = await apiCall("post", "/delete-orders");

      if (data && data.success) {
        fetchFakeOrders();
        toast.success(data.message);
      } else {
        toastFailed("Failed to delete: " + (data?.message || error || "Unknown error"));
      }
    } catch (err) {
      console.error("Delete Error:", err);
      toast.error(err.message || "Failed to delete...");
    } finally{
      setShowModal(false);
    }
  };

  const handleDeleteSingleFakeOrders = async () => {
    // if (!selectedOrderIdToDelete) return;
    console.log("orderId:", selectedOrderIdToDelete)
    try {
      const { data, error } = await apiCall("post", "/delete-single-fake-order", {order_Id: selectedOrderIdToDelete});

      if (data && data.success) {
        fetchFakeOrders();
        toast.success(data.message);
      } else {
        toastFailed("Failed to delete: " + (data?.message || error || "Unknown error"));
      }
    } catch (err) {
      console.error("Delete Error:", err);
      toast.error(err.message || "Failed to delete...");
    } finally {
      setShowDeleteSingleModal(false);
      setSelectedOrderIdToDelete(null);
    }
  };


  return (
    <React.Fragment>
      <Container className="mt-4">
        <section id="admin-buy-order-section">
          <div className="d-flex justify-content-center">
            <h2>Buy Order for Fake User</h2>
          </div>
          <section id="formContent">
            <div className="form">
              <Row>
                <Col lg={6} style={{ margin: "auto" }}>
                  <form onSubmit={handleSubmit}>
                    <div id="formFields">
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <label htmlFor="userId">User Username:</label>
                          <label style={{ textAlign: "end"}}>
                            {loading && userId ? "Checking..." : existUserName?.name}
                          </label>
                        </div>
                        <input
                          type="text"
                          id="userId"
                          className="form-control"
                          value={userId}
                          onChange={(e) => setUserId(e.target.value)}
                          placeholder="Enter user's username"
                          required
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="packageSelect">Select Package:</label>
                        <select
                          id="packageSelect"
                          className="form-control"
                          value={selectedPackage ? selectedPackage.planId : ""}
                          onChange={handlePackageSelect}
                          required
                          style={{ color: 'white' }}
                        >
                          <option value="">-- Select a Package --</option>
                          {packages.map((pkg) => (
                            <option key={pkg.planId} value={pkg.planId}>
                              {pkg.package.name} (Min: {pkg.package.min_amount}, Max: {pkg.package.max_amount})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mb-3">
                        <label htmlFor="amount">Amount:</label>
                        <input
                          type="number"
                          id="amount"
                          className="form-control"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          placeholder="Enter amount"
                          min={minAmount}
                          max={maxAmount}
                          step="any"
                          required
                          disabled={!selectedPackage}
                        />
                        {selectedPackage && (
                          <small className="form-text text-muted">
                            Min: {minAmount}, Max: {maxAmount}
                          </small>
                        )}
                      </div>

                      <div className="mb-3">
                        <label htmlFor="addedDate">Date of Added On:</label>
                        <input
                          type="date"
                          id="addedDate"
                          className="form-control"
                          value={addedDate}
                          onChange={(e) => setAddedDate(e.target.value)}
                          required
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="addedTime">Time of Added On:</label>
                        <input
                          type="time"
                          id="addedTime"
                          className="form-control"
                          value={addedTime}
                          onChange={(e) => setAddedTime(e.target.value)}
                          required
                        />
                      </div>

                      <div className="d-flex justify-content-between mt-4">
                        <Button
                          variant="secondary"
                          className="me-2"
                          onClick={handleClearForm}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="primary"
                          disabled={loading || !selectedPackage || existUserName.color !== "var(--colorSuccess)"}
                        >
                          {loading ? "Placing Order..." : "Place Order"}
                        </Button>
                      </div>
                    </div>
                  </form>
                </Col>
              </Row>
            </div>
          </section>
        </section>

        <section className="mt-5">
          <div className="d-flex justify-content-center mb-3">
            <h2>Fake Orders History</h2>
          </div>

          <Row id="filterRow">
            <span id="filterLogo">
              <div className="d-flex justify-content-between">
                <div onClick={toggleFilter}>
                  <i><BiFilter /></i> Filter
                </div>
              </div>
             <div className="align-content-center">
             <button className="btn btn-danger" onClick={() => setShowModal(true)}>
                Delete All Fake Orders
              </button>

              <ConfirmModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                handleConfirm={handleDeleteAllFakeOrders}
                title="Delete All Fake Orders?"
                body="Are you sure you want to delete all fake orders? This action is permanent and cannot be undone."
              />
            </div>
            </span>

            <Row className="p-0 m-0" style={{ display: filterDisplay }}>
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <input
                    type="text"
                    name="search"
                    placeholder="Search By Name, Username"
                    value={searchParams.search}
                    onChange={handleSearchChange}
                  />
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <input
                    type="text"
                    placeholder="Search User By Uid"
                    name="uid"
                    value={searchParams.uid}
                    onChange={handleSearchChange}
                  />
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <input
                    type="number"
                    name="amount"
                    placeholder="Search By Amount"
                    value={searchParams.amount}
                    onChange={handleSearchChange}
                  />
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <select
                    name="planId"
                    value={searchParams.planId}
                    onChange={handleSearchChange}
                    style={{ color: 'white' }}
                  >
                    <option value="">All Packages</option>
                    {packages.map((pkg) => (
                      <option key={pkg.planId} value={pkg.planId}>
                        {pkg.package.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
              {/* <Col lg="2" md="4" className="mb-2">
                <div>
                  <select
                    name="status"
                    value={searchParams.status}
                    onChange={handleSearchChange}
                    style={{ color: 'white' }}
                  >
                    <option value="">All Status</option>
                    <option value="1">Approved</option>
                    <option value="2">Completed</option>
                    <option value="0">Pending</option>
                  </select>
                </div>
              </Col> */}
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <input
                    type="date"
                    name="startDate"
                    placeholder="Start Date"
                    value={searchParams.startDate}
                    onChange={handleSearchChange}
                  />
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <input
                    type="date"
                    name="endDate"
                    placeholder="End Date"
                    value={searchParams.endDate}
                    onChange={handleSearchChange}
                  />
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <select
                    name="dateFilterQuickSelect"
                    value={dateFilterQuickSelect}
                    onChange={handleDateFilterQuickSelectChange}
                    style={{ color: 'white' }}
                  >
                    <option value="default">Quick Date Filter</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="this_week">This Week</option>
                    <option value="this_month">This Month</option>
                  </select>
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-2">
                <div>
                  <select
                    value={limitFilter}
                    onChange={handleLimitChange}
                    style={{ color: 'white' }}
                  >
                    <option value="20">Show 20</option>
                    <option value="50">Show 50</option>
                    <option value="100">Show 100</option>
                    <option value="200">Show 200</option>
                  </select>
                </div>
              </Col>
              <div>
                <button className="save filter" onClick={applyFilters}>
                  Filter{" "}
                  <i>
                    <BiFilter />
                  </i>
                </button>
                <button className="cancel reset" onClick={resetFilters}>
                  Reset{" "}
                  <i>
                    <GrPowerReset />
                  </i>
                </button>
              </div>
            </Row>
          </Row>

          <div id="directIncomeDiv">
            <Row id="directTableRow" className="text-center">
              <Col md="12" className="p-0">
                <div style={{ width: "100%", overflowX: "auto" }}>
                  <table>
                      <tr>
                        <th>S No.</th>
                        <th>Name</th>
                        <th>Username</th>
                        <th>UID</th>
                        <th>Amount</th>
                        {/* <th>Source</th> */}
                        <th>Package Type</th>
                        {/* <th>Status</th> */}
                        <th>Added On</th>
                        <th>Created At</th>
                        <th>Action</th>
                      </tr>
                    <tbody>
                      {tableLoading ? (
                        <tr>
                          <td colSpan="9">Loading fake orders...</td>
                        </tr>
                      ) : fakeOrders.length > 0 ? (
                        fakeOrders.map((order, i) => (
                          <tr key={order._id}>
                            <td>{i + 1 + (currentPage - 1) * limitFilter}</td>
                            <td>{order.name}</td>
                            <td>{order.username || 'N/A'}</td>
                            <td>{order.uid}</td>
                            <td>{order.amount}</td>
                            <td>{order.package_type}</td>
                            {/* <td>
                              {order.source
                                ? order.source
                                    .split("_")
                                    .join(" ")
                                    .replace(/^./, (char) => char.toUpperCase())
                                : "N/A"}
                            </td> */}
                            {/* <td>{order.status === 1 ? "Approved" : order.status === 2 ? "Completed" : "Pending"}</td> */}
                            <td>{moment(order.added_on).format("LLL")}</td>
                            <td>{moment(order.createdAt).format("LLL")}</td>
                            <td>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => {
                                  setSelectedOrderIdToDelete(order?.order_Id);
                                  setShowDeleteSingleModal(true);
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9">No fake orders found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <ConfirmModal
                      show={showDeleteSingleModal}
                      handleClose={() => setShowDeleteSingleModal(false)}
                      handleConfirm={handleDeleteSingleFakeOrders}
                      title="Delete This Order?"
                      body="Are you sure you want to delete this fake order? This action is permanent and cannot be undone."
                    />
                </div>
              </Col>
            </Row>
          </div>

          <Row className="mt-3">
            <Col md="12">
              <PaginationComponent
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePagination}
              />
            </Col>
          </Row>
        </section>

        <ToastContainer className="toast-container" />
      </Container>
    </React.Fragment>
  );
};

export default AdminBuyOrder;